/**
 * WHAT HAS ACTUALLY BEEN PAID, AND THE PROOF OF IT
 *
 * Deliberately the light version, decided 16-09-2026: no invoice-matching step
 * for contractors. Amount, date, mode, reference — and the bank advice attached
 * to the bill. That is what a company carrying a cash crunch needs: to see at a
 * glance what has gone out against which bill, and to be able to show it.
 *
 * Part payments are normal and expected here. rpc_ra_record_payment refuses to
 * take the total past the net payable, refuses a future date, and refuses a
 * bill that has not cleared its chain.
 *
 * WHY THIS LIVES IN components/ AND NOT INSIDE RaBills.tsx
 *
 * It was inside RaBills.tsx until 16-09-2026, when the contractor payable got a
 * detail screen of its own. Two screens now show the same payments, take the
 * same payment, and attach the same advice. Copying this block would have meant
 * two places to change the day the modal gains a field, and the near-certainty
 * that one of them would be missed — so there is one copy and both screens
 * import it.
 *
 * It takes the four figures it needs rather than a whole RaRow, so the payable
 * screen does not have to fetch a bill it is not displaying.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Banknote } from 'lucide-react'
import { useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import {
  Card, Field, Input, Modal, Select, Stat, useToast,
} from '@/components/ui'
import { saveAttachment } from '@/components/docs/shared'
import {
  listWoPapers, uploadWoPaper, woPaperViewUrl, WO_PAPER_BUCKET, type WoPaper,
} from '../lib/papers'

const n = (v: unknown) => Number(v ?? 0) || 0

export function RaPayments({
  raBillId, label, netPayable, paidToDate, canPay, onChanged,
}: {
  /** The RA bill the payments hang off. Payments are recorded against the
      bill, never against the payable — one door, one RPC, one truth. */
  raBillId: string
  /** What the modal calls this thing. "RA-1" on the bill, "CPAY-…" on the
      payable, because that is the number the reader is looking at. */
  label: string
  netPayable: number
  paidToDate: number
  canPay: boolean
  onChanged: () => void
}) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState('')
  const [mode, setMode] = useState('NEFT')
  const [ref, setRef] = useState('')
  const [remarks, setRemarks] = useState('')
  const [proofs, setProofs] = useState<WoPaper[]>([])

  const today = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])

  const { data: payments, refresh: refreshPayments } = useQuery<any>(
    `ra_pay_${raBillId}`,
    () => supabase.from('v_ra_payments').select('*')
      .eq('ra_bill_id', raBillId).order('payment_date'),
    [raBillId], { cacheable: false })

  const loadProofs = useCallback(async () => {
    setProofs(await listWoPapers(raBillId, 'RA'))
  }, [raBillId])
  useEffect(() => { void loadProofs() }, [loadProofs])

  const paid = n(paidToDate)
  const due  = Math.max(n(netPayable) - paid, 0)

  async function record() {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_ra_record_payment', {
        p_ra_id: raBillId, p_amount: Number(amount), p_date: date,
        p_mode: mode, p_reference: ref.trim() || null,
        p_remarks: remarks.trim() || null,
      })
      if (error) throw new Error(error.message)
      toast.success(`Payment ${data} recorded.`)
      setOpen(false); setAmount(''); setRef(''); setRemarks('')
      refreshPayments(); onChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record that payment')
    } finally { setBusy(false) }
  }

  async function attachProof(file: File) {
    setBusy(true)
    try {
      await uploadWoPaper(raBillId, 'PAYMENT_PROOF', file, 'RA')
      toast.success(`${file.name} attached.`)
      await loadProofs()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That file could not be attached')
    } finally { setBusy(false) }
  }

  return (
    <Card title="Paid against this bill"
          subtitle="What has actually left the bank, and the advice that proves it."
          actions={canPay && due > 0.005 ? (
            <button className="btn-primary btn-sm" onClick={() => { setDate(today); setOpen(true) }}>
              <Banknote className="h-4 w-4" /> Record a payment
            </button>
          ) : undefined}>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Net payable" value={rupees(netPayable)} />
        <Stat label="Paid to date" value={rupees(paid)}
              tone={paid > 0 ? 'good' : 'default'}
              sub={`${(payments as any[]).length} payment${(payments as any[]).length === 1 ? '' : 's'}`} />
        <Stat label="Still owed" value={rupees(due)}
              tone={due > 0.005 ? 'warn' : 'good'}
              sub={due > 0.005 ? undefined : 'settled in full'} />
      </div>

      {(payments as any[]).length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Paid on</th><th>Mode</th><th>Reference</th>
                <th className="num">Amount</th><th>Recorded by</th><th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {(payments as any[]).map((pm) => (
                <tr key={pm.id}>
                  <td className="whitespace-nowrap">{ddmmyyyy(pm.payment_date)}</td>
                  <td className="whitespace-nowrap">{pm.mode}</td>
                  <td className="whitespace-nowrap font-mono text-xs">{pm.reference_no ?? '—'}</td>
                  <td className="num whitespace-nowrap font-semibold">{rupees(pm.amount)}</td>
                  <td className="max-w-[150px] truncate text-xs">{pm.recorded_by_name ?? '—'}</td>
                  <td className="max-w-[200px] truncate text-xs text-slate-500">{pm.remarks ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 border-t border-slate-200 pt-3">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
          Payment advice
        </p>
        {proofs.length === 0 ? (
          <p className="text-xs text-slate-500">
            Nothing attached. The bank advice or transfer screenshot is what an auditor asks
            for when a contractor says he was never paid.
          </p>
        ) : (
          <ul className="space-y-1">
            {proofs.map((f) => (
              <li key={f.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <Banknote className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="min-w-0 flex-1 truncate">{f.file_name}</span>
                <span className="text-xs text-slate-400">{ddmmyyyy(f.uploaded_at)}</span>
                <button className="btn-ghost btn-sm"
                        onClick={() => void woPaperViewUrl(f.file_path)
                          .then((u) => window.open(u, '_blank', 'noopener'))
                          .catch(() => toast.error('That file could not be opened.'))}>
                  Open
                </button>
                <button className="btn-ghost btn-sm"
                        onClick={() => void saveAttachment(WO_PAPER_BUCKET, f.file_path, f.file_name)
                          .catch(() => toast.error('That file could not be downloaded.'))}>
                  Download
                </button>
              </li>
            ))}
          </ul>
        )}
        {canPay && (
          <label className="btn-secondary btn-sm mt-2 cursor-pointer">
            <Banknote className="h-4 w-4" /> Attach the advice
            <input type="file" className="hidden" accept="image/*,application/pdf" disabled={busy}
                   onChange={(e) => {
                     const f = e.target.files?.[0]
                     if (f) void attachProof(f)
                     e.target.value = ''
                   }} />
          </label>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)}
             title="Record a payment"
             subtitle={`${rupees(due)} of ${rupees(netPayable)} is still owed on ${label}. A part payment is fine.`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
                 <button className="btn-primary"
                         disabled={busy || !(Number(amount) > 0) || !date}
                         onClick={() => void record()}>
                   {busy ? 'Recording…' : 'Record it'}
                 </button>
               </>
             }>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount" required
                 hint={Number(amount) > due + 0.005
                   ? `More than the ${rupees(due)} still owed — this will be refused.`
                   : undefined}>
            <Input type="number" step="0.01" min="0.01" value={amount} autoFocus
                   invalid={Number(amount) > due + 0.005}
                   onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Paid on" required hint="Cannot be in the future">
            <Input type="date" max={today} value={date}
                   onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Mode" required>
            <Select value={mode} onChange={(e) => setMode(e.target.value)}
                    options={['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH', 'ADJUSTMENT']
                      .map((m) => ({ value: m, label: m }))} />
          </Field>
          <Field label="Reference" hint="UTR, cheque number, transaction id">
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="UTR0012345678" />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)}
                   placeholder="Part payment released against February bill" />
          </Field>
        </div>
        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Recording a payment updates the payable in the contractor ledger as well, so the
          Payables screen and this bill can never disagree. Attach the bank advice underneath
          once it is recorded.
        </p>
      </Modal>
    </Card>
  )
}
